import { Box, FileDown, Lock, Sparkles } from "lucide-react";
import { ScoreDial } from "@/components/iv/ScoreDial";
import { MathDialog } from "@/components/iv/MathDialog";
import { useApp } from "@/context/AppContext";
import { useCheckout } from "@/context/CheckoutContext";
import { VERDICT_STYLES } from "@/constants/content";
import { downloadReport, usd, usePurchases } from "@/lib/purchases";

export const VerdictBanner = ({ report, onStage, isOwner = false }) => {
  const { priceFor } = useApp();
  const { openCheckout } = useCheckout();
  const ev = report.evaluation;
  const pivot = ev.high_margin_pivot || (ev.recommended_safe_pivot && { title: ev.recommended_safe_pivot.title, formula: ev.recommended_safe_pivot.summary });
  const pdf = priceFor("PDF_REPORT");
  const owned = usePurchases(isOwner ? ev.id : null);
  return (
    <section className="iv-glass grid gap-6 rounded-2xl p-5 sm:p-6 lg:grid-cols-[auto_1fr]" aria-labelledby="verdict-title" data-testid="verdict-banner">
      <div className="flex flex-col items-center gap-3">
        <ScoreDial score={ev.final_score} verdict={ev.verdict} />
        <span className="rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1 text-[11px] font-bold text-cyan-200" data-testid="parameters-tested-badge">
          {report.criteria_scores.length} / 20 Parameters Tested
        </span>
        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${VERDICT_STYLES[ev.verdict]?.pill}`} data-testid="report-verdict-pill">{ev.verdict_label}</span>
        <MathDialog report={report} />
      </div>
      <div className="min-w-0 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Executive Verdict</p>
          <h2 id="verdict-title" className="mt-1 text-lg font-bold text-white" data-testid="report-title">{report.idea?.selected_brand_name || report.idea?.title}</h2>
        </div>
        <div className="rounded-xl border border-blue-400/30 bg-blue-500/5 p-4" data-testid="reality-check">
          <p className="text-[11px] font-bold uppercase tracking-wider text-blue-300">Executive Reality Check</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-200">{ev.executive_reality_check || ev.headline_summary}</p>
        </div>
        {pivot && (
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-4" data-testid="pivot-callout">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-300">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> {ev.requires_safe_pivot ? "Recommended Safe Pivot" : "High-Margin Pivot"} · {pivot.title}
            </p>
            <p className="mt-1 break-words font-mono text-xs leading-relaxed text-emerald-100" data-testid="pivot-formula">{pivot.formula}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => (owned?.pdf ? downloadReport(ev.id) : openCheckout("PDF_REPORT", { evaluationId: isOwner ? ev.id : undefined }))}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-white/30" data-testid="download-pdf-button">
            <FileDown className="h-4 w-4" aria-hidden="true" /> {owned?.pdf ? "Download PDF" : <>Download PDF · {usd(pdf)} <Lock className="h-3 w-3" aria-hidden="true" /></>}
          </button>
          <button type="button" onClick={() => onStage(4)} className="iv-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold text-white" data-testid="open-concept-button">
            <Box className="h-4 w-4" aria-hidden="true" /> Open Free 3D Concept & Test Site
          </button>
        </div>
      </div>
    </section>
  );
};
